import React, { useEffect, useState } from 'react';

import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import { carrierLabel, FULFILLMENT_STATUS, OUTBOUND_STATUS } from '../status';
import type {
  FulfillmentCarrier,
  FulfillmentStatus,
  ManualFulfillmentStatus,
  OutboundOrderStatus,
} from '../types';
import { ActionDialog } from './dialogs';
import { EnumSelect, FormField, TextArea } from './form';
import { codeCls, MappedBadge, metaCls } from './ui';

/** One manually carried order as the shipping list knows it. */
export interface ManualFulfillmentRow {
  outboundOrderID: string;
  code: string;
  waybill: string | null;
  carrier: FulfillmentCarrier | null;
  status: OutboundOrderStatus;
  fulfillmentStatus: FulfillmentStatus | null;
}

/** Order statuses a manual status may be set from (mirrors the server). */
const ALLOWED_FROM: Record<ManualFulfillmentStatus, OutboundOrderStatus[]> = {
  IN_DELIVERY: ['SHIPPED'],
  DELIVERED: ['SHIPPED', 'IN_DELIVERY'],
  FAILED: ['SHIPPED', 'IN_DELIVERY'],
};

const STATUS_OPTIONS: { value: ManualFulfillmentStatus; label: string }[] = [
  { value: 'IN_DELIVERY', label: 'In delivery' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'FAILED', label: 'Failed' },
];

/** A manually carried order whose status ops may still move. */
export const isManualUpdatable = (
  carrier: FulfillmentCarrier | null | undefined,
  status: OutboundOrderStatus | null | undefined
): boolean =>
  !!carrier &&
  carrier !== 'DASH' &&
  (status === 'SHIPPED' || status === 'IN_DELIVERY');

/**
 * Bulk manual status for Lion Parcel orders: every picked order with its AWB
 * and current status, one target status + note for all. Orders the target
 * cannot be set on (already In delivery when picking In delivery, …) are
 * flagged in the table and left out; the server answers per order, so a
 * refusal on one never blocks the rest.
 */
export const BulkFulfillmentDialog: React.FC<{
  rows: ManualFulfillmentRow[] | null;
  onClose: () => void;
  /** Called after the request, whatever the per-item outcome. */
  onDone: () => void;
}> = ({ rows, onClose, onDone }) => {
  const [status, setStatus] = useState<ManualFulfillmentStatus>('DELIVERED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (rows) {
      setNote('');
      setBusy(false);
      // Default to the next natural step of the first row.
      setStatus(rows.some((r) => r.status === 'SHIPPED') ? 'IN_DELIVERY' : 'DELIVERED');
    }
  }, [rows]);

  const allowedFrom = ALLOWED_FROM[status] ?? [];
  const eligible = (rows ?? []).filter((r) => allowedFrom.includes(r.status));
  const skipped = (rows?.length ?? 0) - eligible.length;

  const confirm = async () => {
    if (!eligible.length) return;
    setBusy(true);
    try {
      const { results } = await warehouseApi.bulkUpdateOutboundFulfillment(
        eligible.map((r) => r.outboundOrderID),
        { status, note: note.trim() || null }
      );
      const ok = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      const codeOf = (id: string) =>
        rows?.find((r) => r.outboundOrderID === id)?.code ?? id;
      if (ok.length)
        toast.success(`${ok.length} order diupdate: ${FULFILLMENT_STATUS[status].label}`);
      if (failed.length)
        toast.error(`${failed.length} order gagal diupdate`, {
          description: failed
            .slice(0, 3)
            .map((f) => `${codeOf(f.outboundOrderID)}: ${f.error?.message ?? 'gagal'}`)
            .join(' · '),
        });
      onDone();
    } catch (err) {
      toast.error('Update status gagal', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setBusy(false);
    }
  };

  const single = rows?.length === 1 ? rows[0] : null;
  return (
    <ActionDialog
      open={!!rows}
      title="Update status pengiriman"
      subtitle={
        single
          ? `${single.code} · ${carrierLabel(single.carrier) ?? 'kurir luar'}`
          : `${rows?.length ?? 0} order ${carrierLabel(rows?.[0]?.carrier ?? null) ?? 'kurir luar'}`
      }
      description="Tidak bisa dikembalikan."
      confirmLabel={
        eligible.length > 1
          ? `Update ${eligible.length} order`
          : 'Update status'
      }
      tone={status === 'FAILED' ? 'destructive' : 'primary'}
      icon="ri-truck-line"
      width="lg"
      busy={busy}
      confirmDisabled={!eligible.length}
      disabledReason={
        eligible.length ? undefined : 'Tidak ada order yang bisa ke status ini.'
      }
      testId="bulk-fulfillment-dialog"
      onClose={onClose}
      onConfirm={() => void confirm()}
    >
      <FormField label="Status baru" htmlFor="bf-status" required>
        <EnumSelect
          id="bf-status"
          value={status}
          onChange={(v) => {
            if (v in ALLOWED_FROM) setStatus(v as ManualFulfillmentStatus);
          }}
          options={STATUS_OPTIONS}
        />
      </FormField>
      <div
        className="max-h-64 overflow-y-auto rounded-lg border border-stroke-soft-200"
        data-testid="bulk-fulfillment-rows"
      >
        <table className="w-full text-[12px]">
          <thead className="bg-bg-weak-50 text-left text-text-sub-600">
            <tr>
              <th className="px-3 py-1.5 font-medium">Waybill</th>
              <th className="px-3 py-1.5 font-medium">Order</th>
              <th className="px-3 py-1.5 font-medium">Status sekarang</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => {
              const ok = allowedFrom.includes(r.status);
              return (
                <tr
                  key={r.outboundOrderID}
                  className={`border-t border-stroke-soft-200 ${ok ? '' : 'opacity-50'}`}
                  data-testid={`bf-row-${r.outboundOrderID}`}
                  data-eligible={ok}
                >
                  <td className="px-3 py-1.5">
                    <span className={codeCls}>{r.waybill ?? '—'}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={codeCls}>{r.code}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <MappedBadge map={OUTBOUND_STATUS} value={r.status} />
                    {!ok && (
                      <span className={`${metaCls} ml-2`}>dilewati</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {skipped > 0 && (
        <p className={metaCls} data-testid="bf-skipped">
          {skipped} order dilewati — statusnya tidak bisa ke{' '}
          {FULFILLMENT_STATUS[status].label}.
        </p>
      )}
      <FormField
        label={status === 'FAILED' ? 'Alasan' : 'Catatan'}
        htmlFor="bf-note"
        optional
      >
        <TextArea
          id="bf-note"
          value={note}
          onChange={setNote}
        />
      </FormField>
    </ActionDialog>
  );
};
